/**
 * FotoKtpCard Component
 *
 * Card grid item for displaying a member's profile photo and KTP photo.
 */

import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, ZoomIn } from "lucide-react";
import { KtpPhotoCell } from "./KtpPhotoCell";
import type { FotoKtpMember } from "./FotoKtpGrid";

interface FotoKtpCardProps {
  member: FotoKtpMember;
  onZoom: (src: string, title: string) => void;
  onAction: (member: FotoKtpMember, mode: "reject" | "approve") => void;
  tabMode: "semua" | "hold";
}

function formatPhoneForWhatsApp(phone: string): string {
  let cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("0")) {
    cleaned = "62" + cleaned.substring(1);
  } else if (!cleaned.startsWith("62")) {
    cleaned = "62" + cleaned;
  }
  return cleaned;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function FotoKtpCard({ member, onZoom, onAction, tabMode }: FotoKtpCardProps) {
  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-2 gap-0">
        {/* Profile Photo */}
        <div className="relative aspect-[3/4] bg-muted group">
          {member.foto ? (
            <img
              src={member.foto}
              alt={member.nama_lengkap}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              Tidak ada foto
            </div>
          )}
          <span className="absolute top-2 left-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
            Profil
          </span>
          {member.foto && (
            <Button
              variant="secondary"
              size="sm"
              className="absolute bottom-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onZoom(member.foto!, `Foto Profil - ${member.nama_lengkap}`)}
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* KTP Photo */}
        <div className="relative aspect-[3/4] bg-muted">
          <div className="w-full h-full">
            <KtpPhotoCell
              userId={member._id}
              idcardEncrypted={member.idcardEncrypted}
              idcardUrl={member.idcardUrl}
              userName={member.nama_lengkap}
              fullWidth
            />
          </div>
          <span className="absolute top-2 left-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded z-10">
            KTP
          </span>
        </div>
      </div>

      <CardContent className="p-3 space-y-2">
        {/* Name & Callsign */}
        <div>
          <p className="font-semibold text-sm leading-tight truncate">{member.nama_lengkap}</p>
          <p className="text-xs font-mono text-muted-foreground">{member.callsign}</p>
        </div>

        {/* Status Badge */}
        {member.isFotoValid === false && (
          <Badge variant="destructive" className="text-[10px]">Ditolak</Badge>
        )}
        {tabMode === "hold" && member.fotoRejectionReason && (
          <p className="text-[11px] text-destructive leading-tight line-clamp-2">
            {member.fotoRejectionReason}
          </p>
        )}

        {/* Meta */}
        <div className="text-[11px] text-muted-foreground space-y-0.5">
          <p>NRI: {member.nri || "-"}</p>
          <p>IAR: {member.iar || "-"}</p>
          <p>Upload: {formatDate(member.idcardUploadedAt)}</p>
        </div>

        {/* Contact */}
        {member.no_hp && member.no_hp !== "-" && (
          <a
            href={`https://wa.me/${formatPhoneForWhatsApp(member.no_hp)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-green-600 hover:text-green-700 hover:underline"
          >
            <MessageCircle className="h-3 w-3" />
            {member.no_hp}
          </a>
        )}

        {/* Action Button */}
        {tabMode === "hold" ? (
          <Button
            size="sm"
            className="w-full h-8 text-xs"
            onClick={() => onAction(member, "approve")}
          >
            Lepas Hold
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full h-8 text-xs"
            onClick={() => onAction(member, "reject")}
          >
            Tandai Tidak Memenuhi
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
